import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
    faShoppingBag,
    faClipboardList,
    faCheckCircle,
    faCircleXmark,
    faArrowRight,
    faIndianRupeeSign,
    faBoxOpen
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "./hooks/useHerKart";

// Fallback demo orders — used until "Load Live Orders" is clicked
const sampleOrders = [
  {
    orderNo: "FH20260710001",
    orderedAt: "2026-07-10T10:30:00Z",
    status: "Pending",
    grandTotal: 1139,
    customer: { name: "Priya Sharma", city: "Mumbai" },
  },
  {
    orderNo: "FH20260710002",
    orderedAt: "2026-07-09T16:15:00Z",
    status: "Delivered",
    grandTotal: 447,
    customer: { name: "Anjali Verma", city: "Pune" },
  },
  {
    orderNo: "FH20260710003",
    orderedAt: "2026-07-08T12:45:00Z",
    status: "Cancelled",
    grandTotal: 999,
    customer: { name: "Neha Gupta", city: "Nashik" },
  },
  {
    orderNo: "FH20260707004",
    orderedAt: "2026-07-07T09:10:00Z",
    status: "Delivered",
    grandTotal: 620,
    customer: { name: "Sneha Kulkarni", city: "Nagpur" },
  },
];

const HerKartDashboard = () => {
  const { fetchAllOrders } = useHerKart();

  const [orders, setOrders] = useState(sampleOrders);
  const [isLive, setIsLive] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(true);

  const loadLiveOrders = async () => {
    setLoadingOrders(true);
    try {
      const response = await fetchAllOrders();
      if (response?.success) {
        setOrders(response.orders || []);
        setIsLive(true);
      }
    } catch (e) {
      console.error("Orders fetch error:", e);
    } finally {
      setLoadingOrders(false);
    }
  };

  const loadDemoOrders = () => {
    setOrders(sampleOrders);
    setIsLive(false);
  };

  useEffect(() => {
    loadLiveOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pendingOrders = useMemo(() => {
    return orders.filter((order) => (order.status || "Pending") === "Pending");
  }, [orders]);

  const partiallyDeliveredOrders = useMemo(() => {
    return orders.filter((order) => order.status === "Partially Delivered");
  }, [orders]);

  const deliveredOrders = useMemo(() => {
    return orders.filter((order) => order.status === "Delivered");
  }, [orders]);

  const cancelledOrders = useMemo(() => {
    return orders.filter((order) => order.status === "Cancelled");
  }, [orders]);

  const totalSales = useMemo(() => {
    return deliveredOrders.reduce(
      (sum, order) => sum + (order.grandTotal || 0),
      0,
    );
  }, [deliveredOrders]);

  const recentOrders = useMemo(() => {
    return [...orders]
      .sort((a, b) => new Date(b.orderedAt) - new Date(a.orderedAt))
      .slice(0, 10);
  }, [orders]);

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="flex flex-col mx-auto max-w-7xl px-4 py-2">
          <div className="flex flex-col justify-start">
            <h1 className="text-sm lg:text-xl font-bold text-left">🏪 FoundHer Entreprenure Commerce Dashboard</h1>

            <p className="text-xs lg:text-sm text-slate-200">The Control Center of Private Stores/Product/Services</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={isLive ? loadDemoOrders : loadLiveOrders}
              className="rounded-xl bg-white/10 w-fit px-2 py-1 text-sm hover:bg-white/20"
            >
              {loadingOrders ? "Loading..." : isLive ? "🔄 Demo Orders" : "🔄 Back to Live"}
            </button>

            <Link
              to="/herkart/manage-stores"
              className="rounded-xl border border-pink-500/40 bg-pink-500/10 px-2 py-1 text-xs lg:text-sm font-semibold text-pink-300 hover:bg-pink-500/20"
            >
              Manage My Private Stores
            </Link>

            <Link
              to="/herkart/manage-products"
              className="rounded-xl border border-pink-500/40 bg-pink-500/10 px-2 py-1 text-xs lg:text-sm font-semibold text-pink-300 hover:bg-pink-500/20"
            >
              Manage My Products
            </Link>

            <Link
              to="/herkart/manage-services"
              className="rounded-xl border border-pink-500/40 bg-pink-500/10 px-2 py-1 text-xs lg:text-sm font-semibold text-pink-300 hover:bg-pink-500/20"
            >
              Manage My Services
            </Link>

            <Link
              to="/herkart/feedback"
              className="rounded-xl border border-violet-500/40 bg-violet-500/10 px-2 py-1 text-xs lg:text-sm font-semibold text-violet-300 hover:bg-violet-500/20"
            >
              Feedback
            </Link>

            <Link
              to="/herkart/sample-requests"
              className="rounded-xl border border-violet-500/40 bg-violet-500/10 px-2 py-1 text-xs lg:text-sm font-semibold text-violet-300 hover:bg-violet-500/20"
            >
              Sample/Demo Requests
            </Link>

            <Link
              to="/herkart/orders"
              className="rounded-xl border border-violet-500/40 bg-violet-500/10 px-2 py-1 text-xs lg:text-sm font-semibold text-violet-300 hover:bg-violet-500/20"
            >
              View Orders
            </Link>

            
            <Link
              to="/herkart"
              className="rounded-xl bg-pink-600 px-2 py-1 text-xs lg:text-sm font-semibold hover:bg-pink-500"
            >
              Visit Herkart
            </Link>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6">
        <div className="grid grid-cols-3 lg:grid-cols-5 gap-2">
          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-pink-500/20 to-pink-500/5 p-5">
            <div className="flex items-center justify-between">
              <FontAwesomeIcon icon={faClipboardList} className="text-2xl text-pink-400" />

              <span className="text-3xl font-bold">{pendingOrders.length}</span>
            </div>

            <h3 className="mt-5 text-sm text-slate-400">Pending</h3>
          </div>

          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 p-5">
            <div className="flex items-center justify-between">
              <FontAwesomeIcon icon={faCheckCircle} className="text-2xl text-emerald-400" />

              <span className="text-3xl font-bold">
                {deliveredOrders.length}
              </span>
            </div>

            <h3 className="mt-5 text-sm text-slate-400">Delivered</h3>
          </div>

          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 p-5">
            <div className="flex items-center justify-between">
              <FontAwesomeIcon icon={faBoxOpen} className="text-2xl text-emerald-400" />

              <span className="text-3xl font-bold">
                {partiallyDeliveredOrders.length}
              </span>
            </div>

            <h3 className="mt-5 text-sm text-slate-400">Partially Delivered</h3>
          </div>

          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-red-500/20 to-red-500/5 p-5">
            <div className="flex items-center justify-between">
              <FontAwesomeIcon icon={faCircleXmark} className="text-2xl text-red-400" />

              <span className="text-3xl font-bold">
                {cancelledOrders.length}
              </span>
            </div>

            <h3 className="mt-5 text-sm text-slate-400">Cancelled</h3>
          </div>

          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-violet-500/20 to-violet-500/5 p-5">
            <div className="flex items-center justify-between">
              <FontAwesomeIcon icon={faIndianRupeeSign} className="text-2xl text-violet-400" />

              <span className="text-3xl font-bold">₹{totalSales}</span>
            </div>

            <h3 className="mt-5 text-sm text-slate-400">Sales</h3>
          </div>
        </div>

        <div className="mt-8 rounded-3xl border border-white/10 bg-white/5"></div>
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
          { isLive ? (
            <h2 className="text-xl font-bold text-cyan-200">Recent Orders</h2>
          ):(
            <h2 className="text-xl font-bold text-cyan-200">This is Demo Orders (Click Back To Live For Your Orders)</h2>
          )}

          <Link
            to="/herkart/orders"
            className="flex items-center gap-2 text-sm font-medium text-pink-400 hover:text-pink-300"
          >
            View All
            <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>

        {recentOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-8 text-center">
            <FontAwesomeIcon icon={faShoppingBag} className="mb-5 text-6xl text-slate-200" />

            <h3 className="text-2xl font-bold">No Orders Yet</h3>

            <p className="mt-3 max-w-md text-slate-100">
              {isLive
                ? "Orders placed from the FoundHer marketplace will appear here."
                : "No demo orders to show."}
            </p>

            {isLive && (
              <button
                onClick={loadDemoOrders}
                className="mt-5 rounded-xl bg-pink-600 px-4 py-2 text-sm font-semibold hover:bg-pink-500"
              >
                🔄 Click "Demo Orders" to see how your dashboard will look
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-white/10">
            {recentOrders.map((order, index) => (
              <Link
                key={index}
                to={isLive ? `/herkart/orders/${order.orderNo}` : ""}
                className={`flex flex-col ${isLive ? "border border-green-500" : "border border-dotted border-gray-400"} gap-4 px-6 py-2 transition hover:bg-white/5 md:flex-row md:items-center md:justify-between`}
              >
                <div>
                  <h3 className="font-semibold">#{order.orderNo} - {!isLive ? "Demo": ""}</h3>

                  <p className="mt-1 text-sm text-slate-400">
                    {order.customer?.name}
                  </p>
                </div>

                <div className="text-sm text-slate-400">
                  {order.customer?.city}
                </div>

                <div className="font-semibold">₹{order.grandTotal}</div>

                <div>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      (order.status || "Pending") === "Delivered"
                        ? "bg-emerald-500/20 text-emerald-300"
                        : (order.status || "Pending") === "Cancelled"
                          ? "bg-red-500/20 text-red-300"
                          : "bg-yellow-500/20 text-yellow-300"
                    }`}
                  >
                    {order.status || "Pending"}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HerKartDashboard;